import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Slide,
  alpha,
  LinearProgress
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Mic as MicIcon,
  Article as ArticleIcon,
  Settings as SettingsIcon,
  HourglassEmpty as LoadingIcon,
  RecordVoiceOver as TranscribeIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';

/**
 * Composant NotificationItem - Style iOS/macOS
 * Design sobre et élégant avec auto-disparition
 */
const NotificationItem = ({ notification, index, isStacked = false }) => {
  const { deleteNotification, markAsRead } = useNotifications();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  // Animation d'entrée uniquement - pas d'auto-disparition pour les notifications progress
  useEffect(() => {
    // Animation d'entrée
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, index * 150); // Délai en cascade

    // Les notifications de progression ne disparaissent plus automatiquement
    // Elles restent visibles jusqu'à suppression manuelle ou remplacement par notification de succès

    return () => {
      clearTimeout(showTimer);
    };
  }, [index]);

  // Auto-disparition pour certaines notifications système
  useEffect(() => {
    let autoDismissDelay = 0;
    
    // Notifications de succès (3 secondes)
    if (notification.message === 'Session actualisée automatiquement' && notification.type === 'success') {
      autoDismissDelay = 3000;
    }
    
    // Notifications d'erreur OneDrive (10 secondes - plus long pour lire l'erreur)
    if (notification.type === 'error' && 
        (notification.message.includes('OneDrive') || 
         notification.message.includes('Échec upload') ||
         notification.message.includes('Load failed'))) {
      autoDismissDelay = 10000;
    }
    
    if (autoDismissDelay > 0) {
      const autoDismissTimer = setTimeout(async () => {
        setIsRemoving(true);
        
        setTimeout(async () => {
          try {
            await deleteNotification(notification.id);
          } catch (error) {
            console.error('Erreur lors de la suppression automatique de la notification:', error);
            setIsRemoving(false);
          }
        }, 300);
      }, autoDismissDelay);

      return () => {
        clearTimeout(autoDismissTimer);
      };
    }
  }, [notification.message, notification.type, notification.id, deleteNotification]);

  // Note: Auto-marquage comme "lu" supprimé - les notifications restent "non lues" 
  // jusqu'à ce que l'utilisateur les masque manuellement via l'icône

  // Déterminer le style selon le type
  const getNotificationStyle = () => {
    const { type, category } = notification;
    
    if (type === 'success') {
      return {
        color: '#4CAF50',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#4CAF50',
        icon: <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '1.1rem' }} />
      };
    } else if (type === 'error') {
      return {
        color: '#F44336',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#F44336',
        icon: <ErrorIcon sx={{ color: '#F44336', fontSize: '1.1rem' }} />
      };
    } else if (type === 'warning') {
      return {
        color: '#FF9800',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#FF9800',
        icon: <ErrorIcon sx={{ color: '#FF9800', fontSize: '1.1rem' }} />
      };
    } else if (type === 'progress') {
      // Icône spéciale pour les transcriptions en cours
      if (category === 'transcription') {
        return {
          color: '#3F51B5',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#3F51B5',
          icon: (
            <TranscribeIcon 
              sx={{ 
                color: '#3F51B5', 
                fontSize: '1.1rem',
                animation: 'transcriptionPulse 2s ease-in-out infinite',
                '@keyframes transcriptionPulse': {
                  '0%': {
                    opacity: 0.7,
                    transform: 'scale(1)'
                  },
                  '50%': {
                    opacity: 1,
                    transform: 'scale(1.1)'
                  },
                  '100%': {
                    opacity: 0.7,
                    transform: 'scale(1)'
                  }
                }
              }} 
            />
          )
        };
      }
      
      // Icône spéciale pour les résumés en cours
      if (category === 'summary') {
        return {
          color: '#9C27B0',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#9C27B0',
          icon: (
            <ArticleIcon 
              sx={{ 
                color: '#9C27B0', 
                fontSize: '1.1rem',
                animation: 'summaryRotate 3s linear infinite',
                '@keyframes summaryRotate': {
                  '0%': {
                    transform: 'rotate(0deg) scale(1)',
                    opacity: 0.8
                  },
                  '50%': {
                    transform: 'rotate(180deg) scale(1.05)',
                    opacity: 1
                  },
                  '100%': {
                    transform: 'rotate(360deg) scale(1)',
                    opacity: 0.8
                  }
                }
              }} 
            />
          )
        };
      }
      
      // Icône par défaut pour les autres types de progression
      return {
        color: '#FF9800',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#FF9800',
        icon: <LoadingIcon sx={{ color: '#FF9800', fontSize: '1.1rem' }} />
      };
    }
    
    // Par défaut
    return {
      color: '#2196F3',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#2196F3',
      icon: <CheckCircleIcon sx={{ color: '#2196F3', fontSize: '1.1rem' }} />
    };
  };

  // Formater la date
  const getFormattedDate = () => {
    if (!notification.createdAt) return 'maintenant';
    
    try {
      return formatDistanceToNow(notification.createdAt, {
        addSuffix: true,
        locale: fr
      });
    } catch (error) {
      return 'maintenant';
    }
  };

  // Gérer la suppression avec animation
  const handleRemove = async () => {
    setIsRemoving(true);
    
    setTimeout(async () => {
      try {
        await deleteNotification(notification.id);
      } catch (error) {

        setIsRemoving(false);
      }
    }, 300);
  };

  // Gérer la navigation pour les notifications liées aux réunions
  const handleNavigateToMeeting = async () => {
    const canNavigate = (
      (notification.category === 'transcription' || notification.category === 'summary') && 
      notification.type === 'success' && 
      notification.metadata?.meetingId
    );
    
    if (canNavigate) {
      // Supprimer la notification et naviguer
      await deleteNotification(notification.id);
      navigate(`/meetings/${notification.metadata.meetingId}`);
    }
  };

  const style = getNotificationStyle();

  return (
    <Slide 
      direction="left" 
      in={isVisible && !isRemoving} 
      timeout={400}
      appear
    >
      <Box
        sx={{
          pointerEvents: 'auto', // Permet les interactions sur cette notification
          mb: 1,
          transform: `translateY(${index * -2}px)`, // Léger décalage pour l'effet d'empilement
          zIndex: 9999 - index,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            background: style.backgroundColor,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(style.borderColor, 0.2)}`,
            borderRadius: '12px',
            boxShadow: `
              0 8px 32px ${alpha(style.color, 0.12)}, 
              0 4px 16px ${alpha(style.color, 0.08)},
              0 2px 8px rgba(0, 0, 0, 0.04)
            `,
            overflow: 'hidden',
            maxWidth: '360px',
            minHeight: '64px',
            
            // Barre de couleur à gauche
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              backgroundColor: style.color,
              borderRadius: '0 0 0 12px'
            },

            // Animation d'entrée
            animation: 'notificationSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            '@keyframes notificationSlideIn': {
              '0%': {
                opacity: 0,
                transform: 'translateX(100%) scale(0.95)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateX(0) scale(1)',
              }
            },

            // Hover effects
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `
                0 12px 48px ${alpha(style.color, 0.18)}, 
                0 6px 24px ${alpha(style.color, 0.12)},
                0 3px 12px rgba(0, 0, 0, 0.08)
              `,
              
              '& .notification-close-btn': {
                opacity: 1
              }
            }
          }}
        >
          {/* Bouton de fermeture */}
          <IconButton
            className="notification-close-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleRemove();
            }}
            size="small"
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              opacity: 0,
              transition: 'all 0.2s ease',
              background: 'rgba(0, 0, 0, 0.05)',
              width: 24,
              height: 24,
              '&:hover': {
                background: 'rgba(244, 67, 54, 0.1)',
                '& .MuiSvgIcon-root': {
                  color: '#F44336'
                }
              }
            }}
          >
            <CloseIcon sx={{ fontSize: '0.9rem', color: 'rgba(0,0,0,0.4)' }} />
          </IconButton>

          {/* Contenu principal */}
          <Box 
            onClick={((notification.category === 'transcription' || notification.category === 'summary') && 
                     notification.type === 'success' && 
                     notification.metadata?.meetingId) ? handleNavigateToMeeting : undefined}
            sx={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              p: '12px 40px 12px 16px', // Espace pour le bouton de fermeture
              cursor: ((notification.category === 'transcription' || notification.category === 'summary') && 
                      notification.type === 'success' && 
                      notification.metadata?.meetingId) ? 'pointer' : 'default',
              '&:hover': ((notification.category === 'transcription' || notification.category === 'summary') && 
                         notification.type === 'success' && 
                         notification.metadata?.meetingId) ? {
                backgroundColor: 'rgba(76, 175, 80, 0.05)'
              } : {}
            }}>
            {/* Icône */}
            <Box sx={{ mr: 1.5, mt: 0.5, flexShrink: 0 }}>
              {style.icon}
            </Box>

            {/* Contenu */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Message */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'rgba(0, 0, 0, 0.87)',
                  lineHeight: 1.4,
                  fontSize: '0.875rem'
                }}
              >
                {notification.message}
                {((notification.category === 'transcription' || notification.category === 'summary') && 
                 notification.type === 'success' && 
                 notification.metadata?.meetingId) && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: '#4CAF50',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      mt: 0.5
                    }}
                  >
                    👆 Cliquez pour voir la réunion
                  </Typography>
                )}
              </Typography>

              {/* Informations supplémentaires */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mt: 0.5
              }}>
                {/* Catégorie */}
                <Typography
                  variant="caption"
                  sx={{
                    color: style.color,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textTransform: 'capitalize'
                  }}
                >
                  {notification.category === 'meeting' ? 'Réunion' : 
                   notification.category === 'summary' ? 'Résumé' : 
                   notification.category || 'système'}
                </Typography>

                {/* Date */}
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.5)',
                    fontSize: '0.7rem'
                  }}
                >
                  {getFormattedDate()}
                </Typography>
              </Box>

              {/* Barre de progression pour les tâches en cours */}
              {notification.type === 'progress' && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress 
                    sx={{ 
                      height: 3,
                      borderRadius: 1.5,
                      backgroundColor: alpha(style.color, 0.1),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: style.color
                      }
                    }} 
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Slide>
  );
};

export default NotificationItem; 